import BottomBar from './BottomBar';

import { useEffect, useMemo, useState } from 'react';
import { FileSearch } from 'lucide-react';
import { createRepeatedTaxRecalculator, useRepeatRowsHandler } from '@/lib/table-handlers';
import { getFormActionButtons, getFormTitle, transformFormData } from '../../utils';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { calculateTotals, handleUpdateRowFields } from '../../utils/calc-util';
import { AritoFormV2, FormHeaderActionsV2 } from '@/components/custom/erp';
import { formSchema, FormSchema, initialFormValues } from '../../schema';
import { useDetailRows, useFormFieldState } from '../../hooks';
import { getDocumentsColumns } from './get-documents-columns';
import { useAuth } from '@/contexts/auth-context';
import InfoDropdown from './InfoDropdown';
import BasicInfoTab from './BasicInfoTab';
import EInvoiceTab from './EInvoiceTab';
import { FormMode } from '@/types/form';
import HistoryTab from './HistoryTab';

import GetDocumentsInvoice from '@/components/custom/erp/get-documents-v2';
import DetailTab from './DetailTab';
import OtherTab from './OtherTab';

interface FormDialogProps {
  formMode: FormMode;
  open?: boolean;
  initialData?: BanHangRecord;
  onClose: () => void;
  onAdd?: () => void;
  onEdit?: () => void;
  onDelete?: () => void;
  onCopy?: () => void;
  addItem?: (data: BanHangPayload) => Promise<BanHangRecord>;
  updateItem?: (uuid: string, data: BanHangPayload) => Promise<BanHangRecord>;
  patchItem?: (uuid: string, data: BanHangPayload) => Promise<BanHangRecord>;
  handleCloseForm?: () => void;
  clearSelection?: () => void;
  refreshData?: () => void;
}

const recalculateTaxAfterRepeat = createRepeatedTaxRecalculator<BanHangRecord>({
  getTaxBase: row => Number(row?.tien_nt2 || 0) - Number(row?.ck_nt || 0)
});

const FormDialog = ({
  open,
  formMode,
  initialData,
  onClose,
  onAdd,
  onEdit,
  onDelete,
  onCopy,
  addItem,
  updateItem,
  patchItem,
  handleCloseForm,
  clearSelection,
  refreshData
}: FormDialogProps) => {
  const { entityUnit } = useAuth();
  const [openDialog, setOpenDialog] = useState('none');
  const [activeTab, setActiveTab] = useState<string>('info');
  const {
    rows: detailRows,
    selectedRowUuid: detailSelectedRowUuid,
    selectedCell: detailSelectedCell,
    handleRowClick: detailHandleRowClick,
    handleCellClick: detailHandleCellClick,
    handleAddRow: detailHandleAddRow,
    handleDeleteRow: detailHandleDeleteRow,
    handleCopyRow: detailHandleCopyRow,
    handlePasteRow: detailHandlePasteRow,
    handleMoveRow: detailHandleMoveRow,
    handleCellValueChange: detailHandleCellValueChange,
    setRows: setDetailRows,
    setNonWritableFields: detailSetNonWritableFields,
    setPasteConfigs: detailSetPasteConfigs
  } = useDetailRows(initialData?.chi_tiet || [], undefined, handleUpdateRowFields);
  const { state, actions } = useFormFieldState(initialData);

  const handleRepeatDetailRows = useRepeatRowsHandler({
    setRows: setDetailRows,
    markDirty: () => setOpenDialog(prev => (prev === 'dirty' ? prev : 'dirty')),
    onAfterRepeat: recalculateTaxAfterRepeat
  });

  const { tongSoLuong, tongTienNt2, tongChietKhau, tongThue, tongThanhToan } = useMemo(() => {
    return calculateTotals(detailRows);
  }, [detailRows]);

  useEffect(() => {
    if (!open) {
      setActiveTab('info');
    }
  }, [open]);

  const handleSubmit = async (data: BanHangPayload) => {
    try {
      const formData = transformFormData(
        data,
        state,
        tongSoLuong,
        tongTienNt2,
        tongThanhToan,
        tongThue,
        detailRows,
        entityUnit,
        formMode
      );

      if (formMode === 'add') {
        await addItem?.(formData);
      } else if (formMode === 'edit' && initialData) {
        await updateItem?.(initialData?.uuid, { ...formData });
      }
      handleCloseForm?.();
      clearSelection?.();
      refreshData?.();
      setOpenDialog('none');
    } catch (error) {
      return;
    }
  };

  const handleClose = () => {
    if (openDialog === 'dirty' && formMode !== 'view') {
      setOpenDialog('none');
    } else {
      onClose();
    }
  };

  const handleTabChange = (tabId: string) => {
    setActiveTab(tabId);
  };

  const handleSearchData = (searchedData: BanHangPayload) => {
    const { xprocess, customer, selectedRows } = searchedData;
    if (customer) {
      actions.setKhachHang(customer);
    }
    const updatedRows = selectedRows.map((row: BanHangRecord, index: number) => ({
      uuid: `row-${index}`,
      line: index + 1,
      ma_vt_data: row.ma_vt_data,
      dvt_data: row.dvt_data,
      ma_kho_data: row.ma_kho_data,
      ma_vi_tri_data: row.ma_vi_tri_data,
      so_luong: row.sl_cl,
      sl_cl: row.sl_cl,
      gia_nt2: row.gia_nt2,
      tien_nt2: row.sl_cl * row.gia_nt2,
      ma_thue_data: row.ma_thue_data,
      thue_nt: row.thue_nt,
      tk_thue_no_data: row.tk_thue_co_data,
      id_hd: row.hoa_don_ban_hang_data,
      line_hd: {
        uuid: row.uuid,
        line: row.line
      }
    }));
    let mapRows = [];
    if (xprocess === '1') mapRows = [...detailRows, ...updatedRows];
    else mapRows = updatedRows;

    setDetailRows(mapRows);
  };

  const title = getFormTitle(formMode, activeTab);
  const actionButtons = getFormActionButtons(formMode, activeTab, {
    onAdd,
    onEdit,
    onDelete,
    onCopy,
    handleClose
  });
  return (
    <>
      <AritoFormV2<FormSchema>
        mode={formMode}
        title={title}
        actionButtons={actionButtons}
        subTitle='Hóa đơn điều chỉnh giá hàng bán'
        schema={formSchema}
        initialData={initialData || initialFormValues}
        onSubmit={handleSubmit}
        onClose={onClose}
        headerFields={
          <Tabs value={activeTab} onValueChange={handleTabChange} className='w-full'>
            <div className='flex items-stretch justify-between border-b border-[#d7d7d7] bg-white'>
              <TabsList className='h-8 w-full justify-start rounded-none border-b-0 bg-muted p-0 text-slate-600'>
                <TabsTrigger
                  value='info'
                  className='relative h-8 rounded-none border-b-[3px] border-transparent bg-transparent px-4 py-1 text-[12px] font-semibold text-slate-500 shadow-none ring-0 transition-colors focus-visible:ring-0 data-[state=active]:border-b-main data-[state=active]:bg-main/10 data-[state=active]:text-main data-[state=active]:shadow-none'
                >
                  Thông tin
                </TabsTrigger>
                {formMode === 'view' && (
                  <TabsTrigger
                    value='history'
                    className='relative h-8 rounded-none border-b-[3px] border-transparent bg-transparent px-4 py-1 text-[12px] font-semibold text-slate-500 shadow-none ring-0 transition-colors focus-visible:ring-0 data-[state=active]:border-b-main data-[state=active]:bg-main/10 data-[state=active]:text-main data-[state=active]:shadow-none'
                  >
                    Lịch sử
                  </TabsTrigger>
                )}
              </TabsList>
              <FormHeaderActionsV2
                mode={formMode}
                className='-mr-1'
                source={{
                  label: 'Hóa đơn bán hàng',
                  onClick: () => setOpenDialog('get-documents')
                }}
                info={<InfoDropdown uuid={initialData?.uuid} ma_ct={initialData?.chung_tu} />}
              />
            </div>
            <TabsContent value='info' className='mt-0 outline-none'>
              <div onChange={() => setOpenDialog('dirty')}>
                <BasicInfoTab formMode={formMode} formState={{ state, actions }} />
              </div>
            </TabsContent>
            {formMode === 'view' && (
              <TabsContent value='history' className='mt-0 outline-none'>
                <HistoryTab />
              </TabsContent>
            )}
          </Tabs>
        }
        tabs={
          activeTab === 'info' && [
            {
              id: 'details',
              label: 'Chi tiết',
              component: (
                <DetailTab
                  formMode={formMode}
                  rows={detailRows}
                  selectedRowUuid={detailSelectedRowUuid}
                  selectedCell={detailSelectedCell}
                  onCellClick={detailHandleCellClick}
                  onRowClick={detailHandleRowClick}
                  onAddRow={detailHandleAddRow}
                  onDeleteRow={detailHandleDeleteRow}
                  onCopyRow={detailHandleCopyRow}
                  onPasteRow={detailHandlePasteRow}
                  onMoveRow={detailHandleMoveRow}
                  onRepeatForRows={handleRepeatDetailRows}
                  onCellValueChange={detailHandleCellValueChange}
                  handleUpdateNonWritableFields={detailSetNonWritableFields}
                  handleUpdatePasteConfigs={detailSetPasteConfigs}
                  constraints={state.taiKhoan?.constraints ?? state.khachHang?.account_data?.constraints ?? []}
                />
              )
            },
            {
              id: 'e-invoice',
              label: 'HĐĐT',
              component: <EInvoiceTab formMode={formMode} />
            },
            {
              id: 'other',
              label: 'Khác',
              component: <OtherTab formMode={formMode} formState={{ state, actions }} />
            }
          ]
        }
        bottomBar={
          <BottomBar
            tongSoLuong={tongSoLuong}
            tongTienNt2={tongTienNt2}
            tongChietKhau={tongChietKhau}
            tongThue={tongThue}
          />
        }
      />
      {openDialog === 'get-documents' && (
        <GetDocumentsInvoice
          onSearch={handleSearchData}
          onClose={() => setOpenDialog('none')}
          chungTuType={'HDBH'}
          detailColumns={getDocumentsColumns()}
        />
      )}
    </>
  );
};

export default FormDialog;
